import { useEffect, useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import Image from "next/image";
import { useForm, SubmitHandler } from "react-hook-form";
import {
  FiEye,
  FiTruck,
  FiCalendar,
  FiLifeBuoy,
  FiDollarSign,
} from "react-icons/fi";
import InputGroup from "../common/input-group";
import useAlertBar from "@/hooks/useAlertBar";
import classes from "@/styles/fulfilment-partners/ship-vista-connect.module.css";
import { shipvistaConnect } from "@/utils/content";
import { useIsMobile } from "@/hooks/useIsMobile";
import { shipVistaConnectEmail } from "@/services/connectWithUsServices";
import FileUpload from "../common/FileUpload";

type Inputs = {
  companyName: string;
  contactPerson: string;
  phone: string;
  country: string;
  email: string;
  estimatedAnnualVolume: string;
  customEstimatedAnnualVolume: string | undefined;
  distributionCenterAddress: string;
  shipmentIndustry: string;
  otherIndustry: string | undefined;
  website: string | undefined;
};

const validationSchema = yup.object().shape({
  companyName: yup.string().required("Company Name is required"),
  contactPerson: yup.string().required("Contact Person is required"),
  phone: yup
    .string()
    .matches(/^[0-9]+$/, "Phone number is not valid")
    .min(10, "Phone number is too short")
    .max(10, "Phone number is too long")
    .required("Phone number is required"),
  country: yup.string().required("Country is required"),
  email: yup.string().email("Invalid email").required("Email is required"),
  website: yup.string().url("Invalid URL, HINT: https://www.shipvista.com/"),
  estimatedAnnualVolume: yup
    .string()
    .required("Estimated annual volume is required"),
  customEstimatedAnnualVolume: yup.string().when("estimatedAnnualVolume", {
    is: (val: string) => val === "custom",
    then: (schema) => schema.required("Please specify your estimated volume"),
    otherwise: (schema) => schema.notRequired(),
  }),
  distributionCenterAddress: yup.string().required("Address is required"),
  shipmentIndustry: yup.string().required("Shipment Industry is required"),
  otherIndustry: yup.string().when("shipmentIndustry", {
    is: (val: string) => val === "other",
    then: (schema) => schema.required("Please specify your industry"),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export default function ShipVistaConnect() {
  const [loading, setLoading] = useState(false);
  const [showOtherIndustry, setShowOtherIndustry] = useState(false);
  const [showCustomEstimatedAnnualVolume, setShowCustomEstimatedAnnualVolume] =
    useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const { openAlertBar } = useAlertBar();
  const isMobile = useIsMobile();

  const constants = shipvistaConnect.en;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<Inputs>({
    resolver: yupResolver(validationSchema),
    criteriaMode: "all",

    shouldFocusError: true,
  });

  const shipmentIndustryValue = watch("shipmentIndustry");
  const estimatedAnnualVolumeValue = watch("estimatedAnnualVolume");

  useEffect(() => {
    setShowOtherIndustry(shipmentIndustryValue === "other");
    setShowCustomEstimatedAnnualVolume(estimatedAnnualVolumeValue === "custom");
  }, [shipmentIndustryValue, estimatedAnnualVolumeValue]);

  const onSubmit: SubmitHandler<Inputs> = async (formDataJSON) => {
    setLoading(true);

    const requestBody = {
      ...formDataJSON,
      uploadProofOfAnnualVolume: selectedFile,
    };
    console.log(requestBody);

    const formData = new FormData();
    for (const key in requestBody) {
      formData.append(key, requestBody[key as keyof Inputs] as string | Blob);
    }

    try {
      let { data } = await shipVistaConnectEmail(formData);
      setLoading(false);
      openAlertBar(data.message, data.status);
      reset();
    } catch (error) {
      setLoading(false);
      openAlertBar(
        "Unexpected error occurred while submitting the form. Please try again.",
        false
      );
      console.error(error);
    }
  };

  return (
    <div className={classes.container}>
      <div className={classes.header}>
        <div className={classes.heroImageContainer}>
          <Image
            src="/banner.jpg"
            alt="Header Image"
            layout="fill"
            objectFit="cover"
            quality={100}
            className={`${classes.heroImage} ${
              isMobile ? classes.heroImageMobile : classes.heroImageDesktop
            }`}
          />
          <div className={isMobile ? classes.heroTextMobile : classes.heroText}>
            <h1>{constants.HEADER_TITLE}</h1>
            <p>{constants.HEADER_SUBTITLE}</p>
          </div>
        </div>
      </div>
      <Row className={classes.features}>
        <Col xs={12} md={6} lg={4} className={classes.feature}>
          <FiEye size={isMobile ? 30 : 40} />
          <h3>{constants.TRACKING_VISIBILITY_TITLE}</h3>
          <p>{constants.TRACKING_VISIBILITY_DESC}</p>
        </Col>
        <Col xs={12} md={6} lg={4} className={classes.feature}>
          <FiCalendar size={isMobile ? 30 : 40} />
          <h3>{constants.SERVICE_TITLE}</h3>
          <p>{constants.SERVICE_DESC}</p>
        </Col>
        <Col xs={12} md={6} lg={4} className={classes.feature}>
          <FiLifeBuoy size={isMobile ? 30 : 40} />
          <h3>{constants.SHIPPING_ADVICE_TITLE}</h3>
          <p>{constants.SHIPPING_ADVICE_DESC}</p>
        </Col>
        <Col xs={12} md={6} lg={4} className={classes.feature}>
          <FiTruck size={isMobile ? 30 : 40} />
          <h3>{constants.FREE_PICKUP_TITLE}</h3>
          <p>{constants.FREE_PICKUP_DESC}</p>
        </Col>
        <Col xs={12} md={6} lg={4} className={classes.feature}>
          <FiDollarSign size={isMobile ? 30 : 40} />
          <h3>{constants.PRICING_TITLE}</h3>
          <p>{constants.PRICING_DESC}</p>
        </Col>
      </Row>
      <div className={classes.stats}>
        <h2>{constants.STATS_TEXT}</h2>
      </div>
      <Container className="my-5">
        <Col sm={isMobile ? 12 : 8} xs={12} className="mx-auto">
          <Card className=" shadow-lg">
            <CardBody>
              <Form
                onSubmit={handleSubmit(onSubmit)}
                className={`my-4 needs-validation ${isMobile ? "p-2" : "p-4"}`}
              >
                <h2
                  className={`constants-center mb-4 text-center ${
                    isMobile ? "fs-4" : "fs-2"
                  }`}
                >
                  {constants.FORM_TITLE}
                </h2>
                <Row>
                  <Col xs={12} lg={6}>
                    <InputGroup
                      required
                      label="Company Name"
                      name="companyName"
                      register={register}
                      error={errors.companyName?.message}
                    />
                  </Col>
                  <Col xs={12} lg={6}>
                    <InputGroup
                      required
                      label="Contact Person"
                      name="contactPerson"
                      register={register}
                      error={errors.contactPerson?.message}
                    />
                  </Col>
                  <Col xs={12} lg={6}>
                    <InputGroup
                      required
                      label="Phone"
                      name="phone"
                      type="tel"
                      register={register}
                      error={errors.phone?.message}
                    />
                  </Col>
                  <Col xs={12} lg={6}>
                    <InputGroup
                      required
                      label="Country"
                      name="country"
                      register={register}
                      error={errors.country?.message}
                    />
                  </Col>
                  <Col xs={12} lg={6}>
                    <InputGroup
                      required
                      label="Email"
                      name="email"
                      type="email"
                      register={register}
                      error={errors.email?.message}
                    />
                  </Col>
                  <Col xs={12} lg={6}>
                    <InputGroup
                      label="Website"
                      name="website"
                      type="website"
                      register={register}
                      error={errors.website?.message}
                    />
                  </Col>
                  <InputGroup
                    required
                    label="Estimated Annual Volume"
                    name="estimatedAnnualVolume"
                    type="select"
                    options={constants.ESTIMATED_ANNUAL_VOLUME_OPTIONS}
                    register={register}
                    error={errors.estimatedAnnualVolume?.message}
                  />
                  {showCustomEstimatedAnnualVolume && (
                    <InputGroup
                      required
                      label="Custom Estimated Annual Volume"
                      name="customEstimatedAnnualVolume"
                      type="string"
                      register={register}
                      error={errors.estimatedAnnualVolume?.message}
                    />
                  )}
                  <div>
                    <FileUpload
                      label="Upload Proof Of Annual Volume (Max. 25MB)"
                      name="fileUpload"
                      acceptedFormats={{
                        "application/pdf": [".pdf"],
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                          [".xlsx"],
                        "application/vnd.ms-excel": [".xls"],
                      }}
                      onFileUpload={(file) => setSelectedFile(file)}
                      selectedFile={selectedFile}
                    />
                  </div>
                  <Label>
                    Add a Google Drive link if the file is larger than 25MB.
                  </Label>
                  <InputGroup
                    label="(How to upload file in Google Drive:
                    https://youtu.be/Lx0sozfFVv8)"
                    name="googleDriveLink"
                    register={register}
                  />
                  <InputGroup
                    required
                    label="Distribution Center Address"
                    name="distributionCenterAddress"
                    register={register}
                    error={errors.distributionCenterAddress?.message}
                  />
                  <InputGroup
                    required
                    label="Shipment Industry"
                    name="shipmentIndustry"
                    type="select"
                    options={constants.SHIPMENT_INDUSTRY_OPTIONS}
                    register={register}
                    error={errors.shipmentIndustry?.message}
                  />
                  {showOtherIndustry && (
                    <InputGroup
                      required
                      label="Other Industry"
                      name="otherIndustry"
                      register={register}
                      error={errors.otherIndustry?.message}
                    />
                  )}
                </Row>

                <Button
                  type="submit"
                  disabled={loading}
                  className={`w-100 mt-3 bg-danger ${
                    isMobile ? "py-2" : "py-3"
                  }`}
                >
                  Submit
                  {loading && <Spinner size="sm" className="ms-3" />}
                </Button>
              </Form>
            </CardBody>
          </Card>
        </Col>
      </Container>
    </div>
  );
}
